import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Upload, KeyRound, CheckCircle2, BookOpen } from "lucide-react";
import { api, apiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Loader, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ProductDetail() {
  const { productId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [phases, setPhases] = useState([]);
  const [file, setFile] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/products/${productId}`);
    setP(data);
    if (data.unlocked && productId !== "bundle") {
      const ph = await api.get(`/courses/${productId}/phases`).catch(() => ({ data: [] }));
      setPhases(ph.data);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [productId]);
  if (!p) return <Loader />;

  const submitProof = async () => {
    if (!file) return toast.error("Choose your payment receipt");
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); await api.post(`/products/${productId}/payment-proof`, fd); toast.success("Receipt submitted! Await admin verification."); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const unlock = async () => {
    setBusy(true);
    try { await api.post(`/products/${productId}/unlock`, { code, full_name: user.name, email: user.email }); toast.success("Access unlocked!"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Button variant="ghost" className="rounded-full mb-4 -ml-2" onClick={() => nav("/products")} data-testid="product-back"><ArrowLeft className="w-4 h-4 mr-1.5" /> All Courses</Button>
      <div className="bg-white border border-border rounded-2xl shadow-sm p-6 sm:p-8">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">{p.title}</h1>
        <p className="text-muted-foreground mt-2">{p.description}</p>

        {p.unlocked ? (
          <div className="mt-6" data-testid="product-unlocked">
            <div className="p-4 rounded-xl bg-brandgreen/10 flex items-center gap-2 mb-4"><CheckCircle2 className="w-5 h-5 text-brandgreen" /><p className="text-sm font-medium">You have access to this course.</p></div>
            {productId === "bundle" ? <p className="text-muted-foreground">All additional niche courses are now unlocked. Browse them from the Courses page.</p> : (
              <div className="space-y-2">
                {phases.length === 0 && <p className="text-muted-foreground">Content is being added to this course.</p>}
                {phases.map((ph) => (
                  <div key={ph.id} className="p-4 rounded-xl border border-border flex items-center gap-3"><BookOpen className="w-5 h-5 text-teal" /><span className="font-medium">{ph.title}</span></div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-6 space-y-5">
            <div className="p-5 rounded-xl bg-secondary/50">
              <p className="font-semibold flex items-center gap-2"><Lock className="w-4 h-4 text-teal" /> How to unlock</p>
              <ol className="text-sm text-muted-foreground mt-2 space-y-1 list-decimal list-inside">
                <li>Pay Coach Reyland directly using the details below.</li>
                <li>Submit your payment receipt screenshot here.</li>
                <li>Once verified, you'll receive an access code.</li>
                <li>Enter the code below to unlock instantly.</li>
              </ol>
              {p.payment_details && <div className="mt-3 p-3 rounded-lg bg-white border border-border text-sm whitespace-pre-wrap" data-testid="payment-details">{p.payment_details}</div>}
              {p.price ? <p className="text-teal font-semibold mt-2">Price: {p.price}</p> : null}
            </div>

            <div>
              <Label className="flex items-center gap-1.5"><Upload className="w-4 h-4" /> Submit payment receipt</Label>
              <div className="flex gap-2 mt-1">
                <label className="flex-1 border border-border rounded-xl px-3 py-2 text-sm text-muted-foreground cursor-pointer truncate" data-testid="proof-dropzone">
                  {file ? file.name : "Choose receipt image..."}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="proof-file" />
                </label>
                <Button variant="outline" className="rounded-full" onClick={submitProof} disabled={busy} data-testid="submit-proof">Submit</Button>
              </div>
              {p.proof_status && p.proof_status !== "none" && <div className="mt-2 text-sm">Receipt status: <StatusBadge status={p.proof_status} /></div>}
            </div>

            <div>
              <Label className="flex items-center gap-1.5"><KeyRound className="w-4 h-4" /> Enter access code</Label>
              <div className="flex gap-2 mt-1">
                <Input placeholder="e.g. A1B2C3D4" value={code} onChange={(e) => setCode(e.target.value)} data-testid="unlock-code" />
                <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={unlock} disabled={busy || !code} data-testid="unlock-btn">Unlock</Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">The code is tied to your name & email ({user.email}).</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
